import { classMerge } from "aic";
import { useHierarchyContext } from "contexts/AppSettingsContext";
import { IRequestStatus } from "functions/api/interface";
import { Spinner } from "react-bootstrap";

export interface ArsItemRequestingSpinnerProps {
  requestingCalculation: IRequestStatus<unknown>;
  requestingLabel: string;
}

/**
 * Component to display a spinner when ARS item calculations are being requested or when the clinical library is loading.
 *
 * @param props - The props for the component.
 * @param props.requestingCalculation - The request status of the ARS item calculation.
 * @param props.requestingLabel - The label to display when the ARS item calculation is being requested.
 * @returns A spinner component if either the ARS item calculation is being requested or the clinical library is loading; otherwise, false.
 */
export const ArsItemRequestingSpinner = ({
  requestingCalculation,
  requestingLabel,
}: ArsItemRequestingSpinnerProps) => {
  const hierarchyContext = useHierarchyContext();
  return (
    (requestingCalculation.requesting || hierarchyContext?.isClinicalLibraryLoading) && (
      <Spinner
        variant="primary"
        size="sm"
        animation="border"
        role="status"
        aria-label={`${classMerge([
          requestingCalculation.requesting ? requestingLabel : "",
          hierarchyContext?.isClinicalLibraryLoading ? "Loading clinical library" : "",
        ])}`}
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          zIndex: 1000,
        }}
      />
    )
  );
};

ArsItemRequestingSpinner.displayName = "ArsItemRequestingSpinner";
